// Canonical Buddha KA artist / streaming entity page.
//
// Purpose: give Google (and every other crawler) one authoritative URL where
// the visible HTML, the metadata, and the JSON-LD all agree that buddhaka.com,
// "Buddha KA", the official Spotify artist profile, Apple Music, YouTube and
// the other official profiles are the SAME entity.
//
// Every fact rendered here is repository- or database-sourced. Nothing about
// streaming URLs, release dates, or album metadata is invented.

import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import buddhaKaOfficial from "@/assets/buddha-ka-official.png.asset.json";
import { preloadImageLink } from "@/lib/images";
import { OFFICIAL_ARTIST_PROFILES, STREAMING_LINKS, formatReleaseDate, getCurrentRelease, type Release, type StreamingPlatform } from "@/lib/content";
import { getReleases } from "@/lib/music.functions";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import {
  PERSON_DESCRIPTION,
  PERSON_ID,
  PORTRAIT_URL,
  breadcrumbs,
  imprintRef,
  ldScript,
  musicReleaseNode,
  orgRef,
  personRef,
  releaseId,
  seoMeta,
  websiteRef,
  type SchemaRelease,
} from "@/lib/schema";

const PATH = "/artist/buddha-ka";
const PAGE_URL = absoluteUrl(PATH);
const TITLE = "Buddha KA — Official Artist, Music & Streaming";
const DESCRIPTION =
  "Buddha KA is an independent artist, author, and producer, and the creator of KAfrequency — the ecosystem he built, not a second artist.";
const PORTRAIT_ALT_TEXT = "Buddha KA official artist portrait";

/** Verified artist-level profiles, in canonical display order. Source: src/lib/content.ts */
const PROFILES = OFFICIAL_ARTIST_PROFILES;

const SPOTIFY_ARTIST_URL = STREAMING_LINKS.Spotify;
/** Same artist ID that backs the visible link, the embed, and every sameAs. */
const SPOTIFY_ARTIST_ID = SPOTIFY_ARTIST_URL?.match(/artist\/([A-Za-z0-9]+)/)?.[1];

const SAME_AS = PROFILES.map((p) => p.url);

const ALBUM_LIKE = (r: { type: string }) => r.type !== "Single";

/** Canonical current release — single-sourced from CURRENT_RELEASE_SLUG. */
function latestRelease(releases: Release[]): Release | undefined {
  return getCurrentRelease(releases.filter((r) => r.status === "released"));
}

export const Route = createFileRoute("/artist/buddha-ka")({
  loader: async () => ({ releases: (await getReleases()) as Release[] }),
  head: ({ loaderData }) => {
    const releases = (loaderData?.releases ?? []) as unknown as SchemaRelease[];
    const albums = releases.filter(ALBUM_LIKE);
    const singles = releases.filter((r) => !ALBUM_LIKE(r));

    return {
      ...seoMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        image: PORTRAIT_URL,
        imageAlt: PORTRAIT_ALT_TEXT,
        ogType: "profile",
        extraMeta: [{ name: "robots", content: "index, follow, max-image-preview:large" }],
      }),
      links: [
        { rel: "canonical", href: PAGE_URL },
        preloadImageLink(buddhaKaOfficial.url, "(max-width: 768px) 92vw, 460px"),
      ].filter(Boolean) as Array<Record<string, string>>,
      scripts: [
        // ONE coherent @graph. The artist identity reuses the sitewide
        // canonical @id (src/lib/schema.ts) so no competing duplicate artist
        // entity is created; the MusicGroup node is the same identity, and the
        // WebPage points at it as its mainEntity.
        ldScript({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Person", "MusicGroup"],
              "@id": PERSON_ID,
              name: "Buddha KA",
              url: SITE_URL,
              image: PORTRAIT_URL,
              description: PERSON_DESCRIPTION,
              sameAs: SAME_AS,
              recordLabel: imprintRef(),
              mainEntityOfPage: { "@id": `${PAGE_URL}#webpage` },
              subjectOf: { "@id": `${PAGE_URL}#webpage` },
              ...(albums.length ? { album: albums.map((r) => ({ "@id": releaseId(r.slug) })) } : {}),
              ...(singles.length ? { track: singles.map((r) => ({ "@id": releaseId(r.slug) })) } : {}),
            },
            {
              // ProfilePage is the correct schema.org type for the
              // authoritative page ABOUT a person/creator entity; WebPage is
              // kept alongside it so generic consumers still resolve the node.
              "@type": ["WebPage", "ProfilePage"],
              "@id": `${PAGE_URL}#webpage`,
              url: PAGE_URL,
              name: TITLE,
              description: DESCRIPTION,
              inLanguage: "en",
              isPartOf: websiteRef(),
              primaryImageOfPage: { "@type": "ImageObject", url: PORTRAIT_URL, caption: PORTRAIT_ALT_TEXT },
              mainEntity: personRef(),
              about: [personRef(), orgRef()],
              significantLink: [
                `${SITE_URL}/music`,
                `${SITE_URL}/music/discography`,
                `${SITE_URL}/about`,
                `${SITE_URL}/press`,
              ],
            },
            ...releases.map((r) => musicReleaseNode(r)),
          ],
        }),
        ldScript(breadcrumbs([["Music", "/music"], ["Buddha KA", PATH]])),
      ],
    };
  },
  component: ArtistPage,
});

function ProfileLink({ platform, url }: { platform: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="me noopener noreferrer"
      aria-label={`Buddha KA on ${platform} (opens in a new tab)`}
      className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-5 py-2.5 text-sm text-gold-luminous transition hover:bg-gold hover:text-obsidian"
    >
      {platform}
      <ExternalLink size={12} aria-hidden />
    </a>
  );
}

function ArtistPage() {
  const { releases } = Route.useLoaderData() as { releases: Release[] };
  const latest = latestRelease(releases);
  const albums = releases.filter(ALBUM_LIKE);
  const singles = releases.filter((r) => !ALBUM_LIKE(r));

  return (
    <>
      {/* ── Identity ─────────────────────────────────────────────── */}
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Official Artist</p>
            <h1 className="mt-4 font-serif text-5xl leading-tight sm:text-6xl md:text-7xl">Buddha KA</h1>
            <p className="mt-5 max-w-xl text-lg text-ivory/90">
              This is the official Buddha KA music and streaming page on buddhaka.com — the same artist behind the
              official Spotify, Apple Music, YouTube, SoundCloud, Audiomack, SoundClick, and social profiles listed below.
            </p>
            <p className="mt-5 max-w-xl text-muted-foreground">
              Buddha KA — legal name Curtis Shores — is a Chicago-born independent recording artist, author,
              producer, and creator of <span className="text-ivory">KAfrequency</span>, the artist-owned imprint that releases the
              entire catalog. He works inside his own framework, <span className="text-ivory">KA Flow</span>: hip-hop
              cadence and soul/R&amp;B melody carrying spoken wisdom and spiritual science as a single instrument.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SPOTIFY_ARTIST_URL}
                target="_blank"
                rel="me noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-gold-gradient px-6 py-2.5 text-sm font-semibold text-obsidian"
              >
                Listen on Spotify <ExternalLink size={12} aria-hidden />
              </a>
              <Link
                to="/music"
                className="inline-flex min-h-11 items-center rounded-full border border-gold px-6 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian"
              >
                Buddha KA Music
              </Link>
              <Link
                to="/about"
                className="inline-flex min-h-11 items-center rounded-full border hairline px-6 py-2.5 text-sm hover:border-gold hover:text-gold"
              >
                Full biography
              </Link>
            </div>
          </div>
          <div className="rounded-2xl border border-gold/40 bg-charcoal p-3 shadow-halo">
            <ResponsiveImage
              src={buddhaKaOfficial.url}
              alt={PORTRAIT_ALT_TEXT}
              priority
              sizes="(max-width: 768px) 92vw, 460px"
              className="aspect-[4/5] w-full rounded-xl object-cover"
            />
          </div>
        </div>
      </section>

      {/* ── Official Spotify ─────────────────────────────────────── */}
      {SPOTIFY_ARTIST_URL && (
        <section className="border-b hairline" aria-labelledby="official-spotify">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
            <h2 id="official-spotify" className="font-serif text-3xl text-gold-luminous sm:text-4xl">
              Official Spotify
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              The verified Buddha KA artist profile on Spotify. This is the same artist profile referenced in this
              page&rsquo;s structured data and in the player below.
            </p>
            <p className="mt-6">
              <a
                href={SPOTIFY_ARTIST_URL}
                target="_blank"
                rel="me noopener noreferrer"
                className="break-all text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous"
              >
                {SPOTIFY_ARTIST_URL}
              </a>
            </p>
            {SPOTIFY_ARTIST_ID && (
              <div className="mt-8 overflow-hidden rounded-2xl border border-gold/30 bg-charcoal">
                <iframe
                  title="Buddha KA on Spotify — official artist profile"
                  src={`https://open.spotify.com/embed/artist/${SPOTIFY_ARTIST_ID}?utm_source=generator&theme=0`}
                  width="100%"
                  height="352"
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  className="block w-full border-0"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Current release ──────────────────────────────────────── */}
      {latest && (
        <section className="border-b hairline" aria-labelledby="current-release">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
            <h2 id="current-release" className="font-serif text-3xl text-gold-luminous sm:text-4xl">
              Current Release
            </h2>
            <div className="mt-8 grid gap-8 sm:grid-cols-[240px_minmax(0,1fr)] sm:items-start">
              <ReleaseCover
                coverUrl={latest.coverUrl}
                label={`${latest.title} cover`}
                alt={`${latest.title} — Buddha KA cover art`}
                sizes="(max-width: 640px) 92vw, 240px"
              />
              <div>
                <h3 className="font-serif text-2xl text-ivory">{latest.title}</h3>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-gold">
                  {[latest.type, formatReleaseDate(latest) ?? latest.year].filter(Boolean).join(" · ")}
                </p>
                {latest.statement && <p className="mt-4 text-muted-foreground">{latest.statement}</p>}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/music/$slug"
                    params={{ slug: latest.slug }}
                    className="inline-flex min-h-11 items-center rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian"
                  >
                    Release page
                  </Link>
                  {Object.entries(latest.links ?? {}).map(([platform, url]) => (
                    <ProfileLink key={platform} platform={platform} url={url as string} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Discography ──────────────────────────────────────────── */}
      <section className="border-b hairline" aria-labelledby="discography">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <h2 id="discography" className="font-serif text-3xl text-gold-luminous sm:text-4xl">
            Buddha KA Discography
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Every Buddha KA project released through KAfrequency. Each entry links to its full release page with
            track listing, credits, and verified streaming destinations.
          </p>

          {[
            { label: "Albums, EPs & Mixtapes", items: albums },
            { label: "Singles", items: singles },
          ].map((group) =>
            group.items.length ? (
              <div key={group.label} className="mt-12">
                <h3 className="text-xs uppercase tracking-[0.3em] text-gold">{group.label}</h3>
                <ul className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {group.items.map((r) => (
                    <li key={r.slug}>
                      <Link to="/music/$slug" params={{ slug: r.slug }} className="group block">
                        <ReleaseCover
                          coverUrl={r.coverUrl}
                          label={`${r.title} cover`}
                          alt={`${r.title} — Buddha KA cover art`}
                        />
                        <h4 className="mt-4 font-serif text-xl group-hover:text-gold-luminous">{r.title}</h4>
                      </Link>
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {[r.type, formatReleaseDate(r) ?? r.year].filter(Boolean).join(" · ")}
                      </p>
                      {Object.keys(r.links ?? {}).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {Object.entries(r.links ?? {}).map(([platform, url]) => (
                            <a
                              key={platform}
                              href={url as string}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded-full border hairline px-3 py-1 text-[11px] text-muted-foreground hover:border-gold hover:text-gold"
                            >
                              {platform}
                            </a>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null,
          )}

          <p className="mt-12 text-sm">
            <Link to="/music/discography" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">
              Full Buddha KA discography with track listings
            </Link>
          </p>
        </div>
      </section>

      {/* ── Verified official profiles ───────────────────────────── */}
      <section aria-labelledby="official-profiles">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
          <h2 id="official-profiles" className="font-serif text-3xl text-gold-luminous sm:text-4xl">
            Verified Official Profiles
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Every profile below is an official Buddha KA account, operated by KAfrequency. This list is the complete,
            canonical set of official Buddha KA profiles — if an account is not listed here, it is not official,
            including any duplicate or look-alike profile appearing in search results.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {PROFILES.map((p) => (
              <ProfileLink key={p.platform} platform={p.platform} url={p.url} />
            ))}
          </div>

          <div className="mt-14 border-t hairline pt-8">
            <h2 className="font-serif text-2xl text-gold-luminous">More from Buddha KA</h2>
            <ul className="mt-4 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <li>
                <Link to="/music" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">Buddha KA Music</Link> — the full catalog hub
              </li>
              <li>
                <Link to="/about" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">Buddha KA biography</Link> — origin, KA Flow, KAfrequency
              </li>
              <li>
                <Link to="/wisdom/$slug" params={{ slug: "who-is-buddha-ka" }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">Read the canonical introduction</Link> — who Buddha KA is and how the work fits together
              </li>
              <li>
                <Link to="/press" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">Press kit</Link> — official bios and portrait
              </li>
              <li>
                <Link to="/news" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">Official newsroom</Link> — release announcements
              </li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
